
import React, { useState, useEffect } from 'react';
import { BreadcrumbNavigation } from '@/components/ui/breadcrumb-list';
import { BreadcrumbItemType } from '@/components/ui/breadcrumb-list';
import { Badge } from '@/components/ui/badge';
import { VerifiedBadge } from './VerifiedBadge';
import { MapPin, Phone } from 'lucide-react';
import { GoogleMap } from './GoogleMap';
import { NursingHomeMedia } from './page/NursingHomeMedia';
import { SaveButton } from './card/SaveButton';
import { useMediaQuery } from '@/hooks/use-media-query';
import { useAuth } from '@/components/auth/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { MatchScoreBadge } from './badges/MatchScoreBadge';
import { useMatchScore } from '@/hooks/use-match-score';

interface NursingHomeHeaderProps {
  centerName: string;
  countyName: string;
  countySlug?: string;
  description: string;
  maxResidents?: number | null;
  category?: string;
  nhSlug: string;
  shortDescription?: string;
  address?: string;
  imageUrl?: string;
  phone?: string;
  latitude?: number;
  longitude?: number;
  breadcrumbItems?: BreadcrumbItemType[];
  // For match score
  fairDealApproved?: boolean | null;
  inspectionRating?: string | null;
  wheelchairAccessible?: boolean | null;
  specializations?: string[] | null;
  priceRange?: string | null;
}

export function NursingHomeHeader({
  centerName,
  countyName,
  countySlug,
  category,
  nhSlug,
  shortDescription,
  address,
  imageUrl,
  phone,
  latitude,
  longitude,
  breadcrumbItems,
  fairDealApproved,
  inspectionRating,
  wheelchairAccessible,
  specializations,
  priceRange,
}: NursingHomeHeaderProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const { user } = useAuth();
  const [isInLeads, setIsInLeads] = useState(false);
  const { getScore } = useMatchScore();
  const matchScore = getScore({
    county_slug: countySlug,
    county: countyName,
    fair_deal_approved: fairDealApproved,
    inspection_rating: inspectionRating,
    wheelchair_accessible: wheelchairAccessible,
    specializations,
    category,
    price_range: priceRange,
  });

  // Check if nursing home is in user's leads
  React.useEffect(() => {
    const checkLeads = async () => {
      if (user) {
        const { data, error } = await supabase
          .from("nursing_home_leads")
          .select("id")
          .eq("user_id", user.id)
          .eq("nursing_home_id", nhSlug)
          .limit(1);

        if (!error && data) {
          setIsInLeads(data.length > 0);
        }
      }
    };

    checkLeads();
  }, [user, nhSlug]);

  const handleTrackClick = (type: string) => {
    console.log(`Tracking ${type} for ${centerName}`);
  };

  const getCategoryBadge = () => {
    if (!category) return null;
    const categoryLower = category.trim().toLowerCase();
    if (categoryLower === 'public') {
      return <Badge variant="outline" className="text-sm bg-blue-50 text-blue-700 border-blue-200">
          Public
        </Badge>;
    } else if (categoryLower === 'private') {
      return <Badge variant="outline" className="text-sm bg-amber-50 text-amber-700 border-amber-200">
          Private
        </Badge>;
    } else if (category) {
      return <Badge variant="outline" className="text-sm">
          {category}
        </Badge>;
    }
    return null;
  };

  // Use provided breadcrumb items or generate fallback ones
  const displayBreadcrumbs = breadcrumbItems || [{
    label: 'Nursing Homes',
    href: '/nursing-homes'
  }, {
    label: countyName,
    href: `/nursing-homes/${countySlug}`
  }, {
    label: centerName,
    href: '#'
  }];

  return (
    <>
      {/* Hero image section */}
      {imageUrl && <NursingHomeMedia centerName={centerName} nhSlug={nhSlug} imageUrl={imageUrl} />}
      
      {/* Main header content */}
      <div className="bg-gradient-to-b from-white to-grandpal-beige/20 border-b">
        <div className="container py-4 sm:py-8">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <BreadcrumbNavigation items={displayBreadcrumbs} />
          </div>

          <div className="mt-4 sm:mt-6">
            <div className="grid lg:grid-cols-5 gap-4 sm:gap-8">
              {/* Card with info - stacked on mobile, left column on desktop */}
              <div className="lg:col-span-2 order-1">
                <div className="bg-white rounded-xl p-4 sm:p-6 shadow-md border border-gray-100 h-full flex flex-col">
                  {/* Center name, category and location */}
                  <div className="mb-3 sm:mb-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl sm:text-3xl font-bold text-grandpal-green-dark">{centerName}</h1>
                        {getCategoryBadge()}
                        <VerifiedBadge nhSlug={nhSlug} />
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        <SaveButton
                          nursingHome={{
                            nh_slug: nhSlug,
                            center_name: centerName,
                            county_slug: countySlug || '',
                            county_name: countyName
                          }}
                          isInLeads={isInLeads}
                          onTrackClick={handleTrackClick}
                          className="relative top-0 right-0 bg-transparent border-0 shadow-none hover:bg-gray-50"
                        />
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
                      <div className="flex items-center text-sm sm:text-base text-gray-600">
                        <MapPin className="h-4 w-4 mr-1 text-grandpal-green" />
                        <span>{countyName}, Ireland</span>
                      </div>
                      {matchScore && (
                        <MatchScoreBadge match={matchScore} variant="full" />
                      )}
                    </div>
                  </div>

                  {/* Short description - now with flex-grow to fill space */}
                  {shortDescription && (
                    <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 leading-relaxed flex-grow">
                      {shortDescription}
                    </p>
                  )}
                  
                  {/* Contact information - now at the bottom of the card */}
                  <div className="mt-auto">
                    {phone && (
                      <div className="flex items-center mb-4 sm:mb-6">
                        <Phone className="h-4 w-4 mr-2 text-grandpal-green" />
                        <a 
                          href={`tel:${phone.replace(/\D/g, '')}`} 
                          className="text-sm text-grandpal-gray hover:text-grandpal-green-dark transition-colors py-1"
                        >
                          {phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Map column - full size map without expand option */}
              <div className="lg:col-span-3 order-2">
                {address && (
                  <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 h-full">
                    <div className="h-[490px]">
                      <GoogleMap 
                        address={address} 
                        size="extra-large" 
                        showCard={false} 
                        className="rounded-none h-full w-full" 
                        latitude={latitude}
                        longitude={longitude}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
